/**
 * HustleMeetIDCheckPage.jsx — Live-selfie ID check for HustleMeetups.
 *
 * Flow:
 *   1. Ask for camera + optional geo permission.
 *   2. Show a live preview (video element from getUserMedia).
 *   3. Snap → freeze the frame in a canvas → convert to JPEG blob.
 *   4. POST multipart to /api/hustlemeet-id-check/submit with
 *      device + phone + geo metadata.
 *   5. Show status ("Pending review — approved within 24h").
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Camera, CheckCircle2, RefreshCw, Shield, Loader2 } from "lucide-react";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

const authHeader = () => {
  const t = localStorage.getItem("token") || localStorage.getItem("access_token");
  return t ? { Authorization: `Bearer ${t}` } : {};
};

export default function HustleMeetIDCheckPage() {
  const videoRef  = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [status, setStatus]   = useState("idle");   // idle | streaming | snapped | uploading | done
  const [record, setRecord]   = useState(null);
  const [preview, setPreview] = useState(null);
  const [geo, setGeo]         = useState(null);

  useEffect(() => {
    document.title = "HustleMeetups — Verify your ID";
    (async () => {
      try {
        const { data } = await axios.get(
          `${API}/hustlemeet-id-check/mine`,
          { headers: authHeader(), withCredentials: true });
        if (data?.record?.status === "approved" || data?.record?.status === "pending") {
          setRecord(data.record); setStatus("done");
        }
      } catch (_) {}
    })();
    return () => stopCamera();
  }, []);

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((tk) => tk.stop());
      streamRef.current = null;
    }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: 720, height: 720 },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setStatus("streaming");
      // Best-effort geolocation (user can deny — we still submit).
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (pos) => setGeo({
            lat: pos.coords.latitude,
            lon: pos.coords.longitude,
            acc: pos.coords.accuracy,
          }),
          () => {},
          { enableHighAccuracy: false, timeout: 4000 },
        );
      }
    } catch (e) {
      toast.error(e?.message || "Camera unavailable — please allow camera access.");
    }
  };

  const snap = () => {
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c) return;
    const w = v.videoWidth || 720, h = v.videoHeight || 720;
    c.width = w; c.height = h;
    const ctx = c.getContext("2d");
    ctx.drawImage(v, 0, 0, w, h);
    c.toBlob((blob) => {
      setPreview(URL.createObjectURL(blob));
      setStatus("snapped");
    }, "image/jpeg", 0.85);
  };

  const retake = () => {
    setPreview(null);
    setStatus("streaming");
  };

  const submit = async () => {
    const c = canvasRef.current;
    if (!c) return;
    setStatus("uploading");
    c.toBlob(async (blob) => {
      const form = new FormData();
      form.append("selfie", blob, `selfie_${Date.now()}.jpg`);
      form.append("device_ua", navigator.userAgent || "");
      form.append("device_tz", Intl.DateTimeFormat().resolvedOptions().timeZone || "");
      form.append("device_screen", `${window.innerWidth}x${window.innerHeight}@${window.devicePixelRatio || 1}`);
      if (geo) {
        form.append("geo_lat", String(geo.lat));
        form.append("geo_lon", String(geo.lon));
        form.append("geo_accuracy_m", String(Math.round(geo.acc || 0)));
      }
      try {
        const { data } = await axios.post(
          `${API}/hustlemeet-id-check/submit`, form,
          { headers: { ...authHeader() },
            withCredentials: true });
        setRecord(data.record);
        setStatus("done");
        stopCamera();
        toast.success("Received — an admin will approve within 24 hours.");
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Upload failed");
        setStatus("snapped");
      }
    }, "image/jpeg", 0.85);
  };

  return (
    <div
      data-testid="hustlemeet-id-check-page"
      className="min-h-screen bg-slate-50 text-stone-900 dark:bg-[#0a0908] dark:text-stone-100 transition-colors"
    >
      <PlatformHeader
        brand={{ name: "HustleMeetups — Verify", to: "/meet" }}
        showBookCta={false} showCart={false}
      />

      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20">
        <header className="mb-6 flex items-start gap-3">
          <div className="w-11 h-11 rounded-2xl bg-amber-500/10 text-amber-500 flex items-center justify-center">
            <Shield size={20}/>
          </div>
          <div>
            <p className="text-amber-500 text-[11px] font-black uppercase tracking-[0.32em]">
              Trust and safety
            </p>
            <h1 className="mt-1 text-2xl md:text-3xl font-black">Verify your ID</h1>
            <p className="mt-1 text-sm text-stone-500 dark:text-stone-300 max-w-md">
              A quick 60-second selfie confirms you are a real person. Your photo is stored
              privately, never shown to other members, and an admin reviews it within 24 hours.
            </p>
          </div>
        </header>

        {status === "done" && record ? (
          <div
            data-testid="idcheck-done"
            className="rounded-2xl border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-6"
          >
            <CheckCircle2 size={28} className="text-emerald-500" />
            <p className="mt-3 font-black">
              {record.status === "approved" ? "You are verified." : "Received — pending review."}
            </p>
            <p className="mt-1 text-sm text-stone-600 dark:text-stone-300">
              {record.status === "approved"
                ? "The verified badge is now visible on your Circles and Matchmaker profile."
                : "Most checks are approved within a few hours. You'll get an email as soon as it's live."}
            </p>
            <p className="mt-3 text-[11px] text-stone-500">
              Record ID: <code>{record.id}</code>
            </p>
          </div>
        ) : (
          <div className="rounded-2xl border border-stone-200 dark:border-stone-800 bg-white dark:bg-slate-950 p-6">
            <div
              data-testid="idcheck-viewport"
              className="relative aspect-square rounded-xl overflow-hidden bg-stone-900 flex items-center justify-center"
            >
              {status === "idle" && (
                <div className="text-center text-stone-400">
                  <Camera size={40} className="mx-auto mb-3 opacity-60"/>
                  <p className="text-sm">Camera preview will appear here</p>
                </div>
              )}
              {(status === "streaming" || status === "uploading") && !preview && (
                <video
                  ref={videoRef}
                  playsInline muted
                  className="w-full h-full object-cover"
                />
              )}
              {preview && (
                <img src={preview} alt="Selfie preview"
                     className="w-full h-full object-cover"/>
              )}
              <canvas ref={canvasRef} className="hidden" />
            </div>

            <div className="mt-4 flex flex-wrap gap-2 justify-end">
              {status === "idle" && (
                <Button
                  onClick={startCamera}
                  data-testid="btn-start-camera"
                  className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black"
                >
                  <Camera size={14} className="mr-1"/> Turn on camera
                </Button>
              )}
              {status === "streaming" && (
                <Button
                  onClick={snap}
                  data-testid="btn-snap"
                  className="rounded-full bg-amber-500 text-slate-950 hover:bg-amber-400 font-black"
                >
                  Snap
                </Button>
              )}
              {status === "snapped" && (
                <>
                  <Button
                    onClick={retake} variant="outline"
                    data-testid="btn-retake"
                    className="rounded-full font-black"
                  >
                    <RefreshCw size={14} className="mr-1"/> Retake
                  </Button>
                  <Button
                    onClick={submit}
                    data-testid="btn-submit-idcheck"
                    className="rounded-full bg-emerald-500 text-slate-950 hover:bg-emerald-400 font-black"
                  >
                    <CheckCircle2 size={14} className="mr-1"/> Submit
                  </Button>
                </>
              )}
              {status === "uploading" && (
                <Button disabled className="rounded-full font-black">
                  <Loader2 size={14} className="mr-1 animate-spin"/> Uploading…
                </Button>
              )}
            </div>

            <p className="mt-4 text-[11px] text-stone-500 dark:text-stone-400">
              We capture device model, browser and (optional) coarse location alongside your
              selfie for anti-fraud. We do not share this with other members or third parties.
              Verified badges keep every Circle safe.
            </p>
          </div>
        )}
      </div>

      <SuiteFooter />
    </div>
  );
}
